import { useEffect, useState } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ChevronDown, Clock, BookOpen, AlertTriangle, ArrowRight } from "lucide-react";
import { getLesson, relatedLessons, CATEGORY_LABELS } from "@/data/lessons";
import { LessonDiagram } from "@/components/landing/LessonDiagram";
import { useSeo, JsonLd, lessonJsonLd } from "@/lib/seo";

const Faq = ({ q, a, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.07] last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`lesson-faq-${id}`}
        className="group flex w-full items-center gap-3 py-4 text-left"
      >
        <span className={`font-term text-sm leading-snug transition-colors ${open ? "text-[#BACD1C]" : "text-white/80 group-hover:text-white"}`}>{q}</span>
        <ChevronDown size={16} className={`ml-auto shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#BACD1C]" : "text-white/30"}`} />
      </button>
      {open && <p className="pb-4 pr-4 text-[13px] text-white/55 leading-relaxed">{a}</p>}
    </div>
  );
};

export default function Lesson() {
  const { slug } = useParams();
  const lesson = getLesson(slug);

  useSeo({
    title: lesson ? `${lesson.title} — TradrQuant Academy` : "TradrQuant Academy",
    description: lesson ? lesson.summary : "Free chart-reading lessons from TradrQuant Academy.",
    path: `/learn/${slug}`,
    type: "article",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!lesson) return <Navigate to="/learn" replace />;

  const related = relatedLessons(lesson);
  const catLabel = CATEGORY_LABELS[lesson.category];

  return (
    <div data-testid="lesson-page" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <JsonLd data={lessonJsonLd(lesson, catLabel)} />
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link
            to="/learn"
            data-testid="lesson-back-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300"
          >
            <ChevronLeft size={14} /> Academy
          </Link>
        </div>
      </header>

      {/* breadcrumb */}
      <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-8">
        <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          <Link to="/" className="hover:text-[#BACD1C]">Home</Link>
          <ChevronRight size={11} />
          <Link to="/learn" className="hover:text-[#BACD1C]">Academy</Link>
          <ChevronRight size={11} />
          <span className="text-[#BACD1C]/70">{catLabel}</span>
        </nav>
      </div>

      {/* hero */}
      <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-6 md:pt-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">// {lesson.category}.lesson</p>
        <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[1.02] max-w-3xl">
          {lesson.title}
        </h1>
        <p className="mt-5 max-w-2xl text-sm text-white/55 leading-relaxed">{lesson.summary}</p>
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">
          <span className="flex items-center gap-1.5"><Clock size={12} /> {lesson.readMins} min read</span>
          <span className="flex items-center gap-1.5"><BookOpen size={12} className="text-[#BACD1C]/60" /> {catLabel}</span>
        </div>
      </div>

      <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-10 md:py-14 space-y-10">
        {/* key stats */}
        {lesson.stats && (
          <div data-testid="lesson-stats" className="grid grid-cols-2 md:grid-cols-4 border border-white/12 bg-[#13151A] divide-x divide-white/10">
            {lesson.stats.map((s, i) => (
              <div key={i} className={`p-5 ${i >= 2 ? "border-t md:border-t-0 border-white/10" : ""}`}>
                <div className="font-display font-semibold text-[#ECEBE4] text-2xl sm:text-3xl tracking-[-0.03em] tabular-nums">{s.value}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* takeaways */}
        <div className="relative border-l-2 border-white/[0.10] bg-white/[0.03] pl-5 pr-4 py-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#7D8390]">// key takeaways</p>
          <ul className="mt-4 space-y-2.5">
            {lesson.takeaways.map((t, i) => (
              <li key={i} className="flex gap-2.5 font-mono text-[13px] text-white/70 leading-snug">
                <span className="text-[#BACD1C] mt-0.5 shrink-0">▸</span> {t}
              </li>
            ))}
          </ul>
        </div>

        {/* diagram */}
        {lesson.diagram && (
          <div className="max-w-xl">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/30">// figure: {lesson.title.toLowerCase()}</p>
            <LessonDiagram d={lesson.diagram} />
          </div>
        )}

        {/* body blocks */}
        <article className="space-y-10">
          {lesson.blocks.map((b, i) => (
            <motion.section
              key={i}
              data-testid={`lesson-block-${i}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45 }}
            >
              <h2 className="font-display font-semibold tracking-[-0.03em] text-xl sm:text-2xl leading-tight">{b.h}</h2>
              {b.p && (
                <div className="mt-4 space-y-4">
                  {b.p.map((para, pi) => (
                    <p key={pi} className="text-[13px] sm:text-sm text-white/60 leading-relaxed">{para}</p>
                  ))}
                </div>
              )}
              {b.list && (
                <ul className="mt-4 space-y-2.5 border-l border-white/10 pl-5">
                  {b.list.map((li, idx) => (
                    <li key={idx} className="flex gap-2.5 text-[13px] text-white/60 leading-relaxed">
                      <span className="text-[#BACD1C] mt-0.5 shrink-0">›</span> {li}
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </article>

        {/* mistakes */}
        {lesson.mistakes && (
          <div className="border border-red-400/20 bg-red-500/[0.03] p-6">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-red-300/80">
              <AlertTriangle size={14} /> // common mistakes
            </p>
            <ul className="mt-4 space-y-2.5">
              {lesson.mistakes.map((m, i) => (
                <li key={i} className="flex gap-2.5 font-mono text-[13px] text-white/60 leading-snug">
                  <span className="text-red-400/70 mt-0.5 shrink-0">✕</span> {m}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* faqs */}
        {lesson.faqs && (
          <div data-testid="lesson-faqs">
            <h2 className="font-display font-semibold tracking-[-0.03em] text-xl sm:text-2xl">Frequently asked</h2>
            <div className="mt-4 border border-white/12 bg-[#13151A] px-5">
              {lesson.faqs.map((f, i) => (
                <Faq key={i} id={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        )}

        {/* CTA to detection */}
        <div className="relative border border-white/[0.10] bg-white/[0.03] p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-display font-semibold tracking-[-0.03em] text-lg">See it live in the engine</p>
            <p className="mt-1 font-mono text-[13px] text-white/55">TradrQuant scans 800+ markets for these structures 24/7 — free on Telegram.</p>
          </div>
          <Link
            to="/#free"
            data-testid="lesson-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-white active:scale-[0.98] transition-[background-color,transform] duration-300"
          >
            Get free alerts <ArrowRight size={15} />
          </Link>
        </div>

        {/* related */}
        {related.length > 0 && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/30 mb-4">// related lessons</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/learn/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group border border-white/10 bg-[#13151A] p-5 hover:border-[#BACD1C]/40 transition-colors duration-300"
                >
                  <p className="font-display font-semibold tracking-[-0.03em] text-base group-hover:text-[#BACD1C] transition-colors">{r.title}</p>
                  <p className="mt-2 font-mono text-[12px] text-white/45 leading-snug line-clamp-2">{r.summary}</p>
                  <span className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">Read <ArrowRight size={11} /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <p className="font-mono text-[11px] text-white/25 leading-relaxed">
          Educational content only — not financial advice. Pattern statistics reference Thomas
          Bulkowski (thepatternsite.com) and published technical-analysis literature.
        </p>
      </div>
      <Footer />
    </div>
  );
}
